import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { apiInstance } from '../../axiosApi';

export const crmsListGet = createAsyncThunk('configuration/crms', async (params) => {
  return await apiInstance.get('/crms', { params });
});

export const crmsDetailsGetById = createAsyncThunk('configuration/crms-by-id', async (id) => {
  return await apiInstance.get(`/crms/${id}`);
});

export const crmsAdd = createAsyncThunk('configuration/crms-add', async (payload) => {
  return await apiInstance.post('/crms', payload);
});

export const crmsUpdate = createAsyncThunk('configuration/crms-update', async (payload) => {
  const { id, ...rest } = payload;
  return await apiInstance.put(`/crms/${id}`, rest);
});

export const crmsDelete = createAsyncThunk('configuration/crms-delete', async (id) => {
  return await apiInstance.delete(`/crms/${id}`);
});

export const userListGet = createAsyncThunk('configuration/user', async (params) => {
  return await apiInstance.get('/user', { params });
});

export const userDelete = createAsyncThunk('configuration/user-delete', async (id) => {
  return await apiInstance.delete(`/user/${id}`);
});

export const userAdd = createAsyncThunk('configuration/user-add', async (payload) => {
  return await apiInstance.post('/user', payload);
});

export const userUpdate = createAsyncThunk('configuration/user-update', async (payload) => {
  const { id, ...rest } = payload;
  return await apiInstance.put(`/user/${id}`, rest);
});

export const breakListGet = createAsyncThunk('configuration/breaks', async (params) => {
  return await apiInstance.get('/breaks', { params });
});

export const breakDelete = createAsyncThunk('configuration/breaks-delete', async (id) => {
  return await apiInstance.delete(`/breaks/${id}`);
});

export const breakAdd = createAsyncThunk('configuration/breaks-add', async (payload) => {
  return await apiInstance.post('/breaks', payload);
});

export const breakUpdate = createAsyncThunk('configuration/breaks-update', async (payload) => {
  const { id, ...rest } = payload;
  return await apiInstance.put(`/breaks/${id}`, rest);
});

export const leadRetriesListGet = createAsyncThunk('configuration/lead-retries', async (params) => {
  return await apiInstance.get('/lead-retries', { params });
});

export const leadRetriesDelete = createAsyncThunk(
  'configuration/lead-retries-delete',
  async (id) => {
    return await apiInstance.delete(`/lead-retries/${id}`);
  }
);

export const leadRetriesAdd = createAsyncThunk(
  'configuration/lead-retries-add',
  async (payload) => {
    return await apiInstance.post('/lead-retries', payload);
  }
);

export const leadRetriesUpdate = createAsyncThunk(
  'configuration/lead-retries-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/lead-retries/${id}`, rest);
  }
);

export const processStatusUpdate = createAsyncThunk(
  'configuration/process-status-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/lists/process_status_update/${id}`, rest);
  }
);

export const auditorStatusUpdate = createAsyncThunk(
  'configuration/update-auto-audit',
  async (payload) => {
    return await apiInstance.put(`/process/update-auto-audit`, payload);
  }
);

export const processListGet = createAsyncThunk('configuration/process', async (params) => {
  return await apiInstance.get('/process', { params });
});

export const processDelete = createAsyncThunk('configuration/process-delete', async (id) => {
  return await apiInstance.delete(`/process/${id}`);
});

export const processAdd = createAsyncThunk('configuration/process-add', async (payload) => {
  return await apiInstance.post('/process', payload);
});

export const processUpdate = createAsyncThunk('configuration/process-update', async (payload) => {
  const { id, ...rest } = payload;
  return await apiInstance.put(`/process/${id}`, rest);
});

export const processDetailsGetById = createAsyncThunk('configuration/process-by-id', async (id) => {
  return await apiInstance.get(`/process/${id}`);
});

export const processCopy = createAsyncThunk('configuration/process-copy', async (payload) => {
  return await apiInstance.post('/process/copy-process', payload);
});

export const activeListGet = createAsyncThunk('configuration/lists', async (params) => {
  return await apiInstance.get('/lists', { params });
});

export const inActiveListGet = createAsyncThunk('configuration/lists/inactive', async (params) => {
  return await apiInstance.get('/lists/inactive', { params });
});

export const listStatusUpdate = createAsyncThunk(
  'configuration/list-status-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/lists/status_update/${id}`, rest);
  }
);

export const listAdd = createAsyncThunk('configuration/lists', async (payload) => {
  return await apiInstance.post('/lists', payload);
});

export const listUpdate = createAsyncThunk('configuration/lists-update', async (payload) => {
  const { id, ...rest } = payload;
  return await apiInstance.put(`/lists/${id}`, rest);
});

export const listDetailsGetById = createAsyncThunk('configuration/lists-by-id', async (id) => {
  return await apiInstance.get(`/lists/${id}`);
});

export const listDelete = createAsyncThunk('configuration/lists-delete', async (id) => {
  return await apiInstance.delete(`/lists/${id}`);
});

export const listBadNumbers = createAsyncThunk('configuration/lists/bad-numbers', async (id) => {
  return await apiInstance.get(`/lists/bad-numbers/${id}`, { responseType: 'blob' });
});

export const listUploadDetails = createAsyncThunk(
  'configuration/lists/lists_upload',
  async (id) => {
    return await apiInstance.get(`/lists/lists_upload/${id}`);
  }
);

export const mappingList = createAsyncThunk('configuration/mapping_list', async (payload) => {
  const { id, process_id } = payload;
  return await apiInstance.get(`/lists/mapping_list/${id}`, { params: { process_id } });
});

export const listUploadCsv = createAsyncThunk('configuration/upload_csv', async (payload) => {
  return await apiInstance.post('/lists/upload_csv', payload);
});

export const mappingListGet = createAsyncThunk('configuration/lists/mapping_list', async () => {
  return await apiInstance.get('/lists/mapping_list');
});

export const listMappingSelected = createAsyncThunk(
  'configuration/lists/mapping_list_selected',
  async (id) => {
    return await apiInstance.get(`/lists/mapping_list_selected/${id}`);
  }
);

export const mappingListAdd = createAsyncThunk(
  'configuration/lists/mapping_list/add',
  async (payload) => {
    return await apiInstance.post('/lists/mapping_list', payload);
  }
);

export const resetListGetById = createAsyncThunk('configuration/lists/reset_list', async (id) => {
  return await apiInstance.get(`/lists/reset_list/${id}`);
});

export const resetListUpdate = createAsyncThunk(
  'configuration/lists/reset_list',
  async (payload) => {
    return await apiInstance.post('/lists/reset_list', payload);
  }
);

export const trunkListGet = createAsyncThunk('configuration/trunks', async (params) => {
  return await apiInstance.get('/trunks', { params });
});

export const listsDownload = createAsyncThunk('configuration/lists/download', async (params) => {
  return await apiInstance.get('/lists/download', { params, responseType: 'blob' });
});

export const listsPreview = createAsyncThunk(
  'configuration/lists/details-status-report',
  async (params) => {
    return await apiInstance.get('/lists/details-status-report', { params });
  }
);

export const listsDeleteCrm = createAsyncThunk('configuration/lists/delete-crm', async (params) => {
  return await apiInstance.delete('/lists/delete-crm', { params });
});

export const trunkOptionGet = createAsyncThunk('configuration/trunks', async () => {
  return await apiInstance.get('/trunks');
});

export const sipProfileOptionGet = createAsyncThunk(
  'configuration/trunks/get_sip_profile',
  async () => {
    return await apiInstance.get('/trunks/get_sip_profile');
  }
);

export const trunkDelete = createAsyncThunk('configuration/trunks-delete', async (id) => {
  return await apiInstance.delete(`/trunks/${id}`);
});

export const trunkAdd = createAsyncThunk('configuration/trunks-add', async (payload) => {
  return await apiInstance.post('/trunks', payload);
});

export const trunkUpdate = createAsyncThunk('configuration/trunks-update', async (payload) => {
  const { id, ...rest } = payload;
  return await apiInstance.put(`/trunks/${id}`, rest);
});

export const outboundListGet = createAsyncThunk('configuration/outboundroutes', async (params) => {
  return await apiInstance.get('/outboundroutes', { params });
});

export const outboundDelete = createAsyncThunk(
  'configuration/outboundroutes-delete',
  async (id) => {
    return await apiInstance.delete(`/outboundroutes/${id}`);
  }
);

export const outboundAdd = createAsyncThunk('configuration/outboundroutes-add', async (payload) => {
  return await apiInstance.post('/outboundroutes', payload);
});

export const outboundUpdate = createAsyncThunk(
  'configuration/outboundroutes-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/outboundroutes/${id}`, rest);
  }
);

export const outboundGetById = createAsyncThunk(
  'configuration/outboundroutes-by-id',
  async (id) => {
    return await apiInstance.get(`/outboundroutes/${id}`);
  }
);

export const outboundOptionGet = createAsyncThunk('configuration/outboundroutes', async () => {
  return await apiInstance.get('/outboundroutes');
});

export const processQueuesAssignedListGet = createAsyncThunk(
  'configuration/queues/assigned',
  async (params) => {
    return await apiInstance.get('/queues/assigned', { params });
  }
);

export const processQueuesNotAssignedListGet = createAsyncThunk(
  'configuration/queues/not-assigned',
  async (params) => {
    return await apiInstance.get('/queues/not-assigned', { params });
  }
);

export const queueProcessAssign = createAsyncThunk(
  'configuration/queues/queue-process',
  async (payload) => {
    return await apiInstance.post('/queues/queue-process', payload);
  }
);

export const processDispositionAssignedListGet = createAsyncThunk(
  'configuration/assign-disposition/assigned',
  async (params) => {
    return await apiInstance.get('/assign-disposition/assigned', { params });
  }
);

export const processDispositionNotAssignedListGet = createAsyncThunk(
  'configuration/assign-disposition/not-assigned',
  async (params) => {
    return await apiInstance.get('/assign-disposition/not-assigned', { params });
  }
);

export const dispositionProcessAssign = createAsyncThunk(
  'configuration/assign-disposition/disposition-assign-process',
  async (payload) => {
    return await apiInstance.post('/assign-disposition/disposition-assign-process', payload);
  }
);

export const processAuditorAssignedListGet = createAsyncThunk(
  'configuration/assign-auditor-category/assigned',
  async (params) => {
    return await apiInstance.get('/assign-auditor-category/assigned', { params });
  }
);

export const processAuditorNotAssignedListGet = createAsyncThunk(
  'configuration/assign-auditor-category/not-assigned',
  async (params) => {
    return await apiInstance.get('/assign-auditor-category/not-assigned', { params });
  }
);

export const auditorProcessAssign = createAsyncThunk(
  'configuration/assign-auditor-category/category-process',
  async (payload) => {
    return await apiInstance.post('/assign-auditor-category/category-process', payload);
  }
);

export const processWhatsappAssignedListGet = createAsyncThunk(
  'configuration/assign-whatsapp-number/assigned',
  async (params) => {
    return await apiInstance.get('/assign-whatsapp-number/assigned', { params });
  }
);

export const processWhatsappNotAssignedListGet = createAsyncThunk(
  'configuration/assign-whatsapp-number/not-assigned',
  async (params) => {
    return await apiInstance.get('/assign-whatsapp-number/not-assigned', { params });
  }
);

export const whatsappProcessAssign = createAsyncThunk(
  'configuration/assign-whatsapp-number/whatsapp-assign-process',
  async (payload) => {
    return await apiInstance.post('/assign-whatsapp-number/whatsapp-assign-process', payload);
  }
);

export const processMetaAssignedListGet = createAsyncThunk(
  'configuration/assign-whatsapp-meta-number/assigned',
  async (params) => {
    return await apiInstance.get('/assign-whatsapp-meta-number/assigned', { params });
  }
);

export const processMetaNotAssignedListGet = createAsyncThunk(
  'configuration/assign-whatsapp-meta-number/not-assigned',
  async (params) => {
    return await apiInstance.get('/assign-whatsapp-meta-number/not-assigned', { params });
  }
);

export const metaProcessAssign = createAsyncThunk(
  'configuration/assign-whatsapp-meta-number/whatsapp-assign-process',
  async (payload) => {
    return await apiInstance.post(
      '/assign-whatsapp-meta-number/whatsapp-meta-assign-process',
      payload
    );
  }
);

export const usersQueuesAssignedListGet = createAsyncThunk(
  'configuration/queues/user-assigned',
  async (params) => {
    return await apiInstance.get('/queues/user-assigned', { params });
  }
);

export const usersQueuesNotAssignedListGet = createAsyncThunk(
  'configuration/queues/user-not-assigned',
  async (params) => {
    return await apiInstance.get('/queues/user-not-assigned', { params });
  }
);

export const userProcessAssign = createAsyncThunk(
  'configuration/queues/queue-user',
  async (payload) => {
    return await apiInstance.post('/queues/queue-user', payload);
  }
);

export const scriptsListGet = createAsyncThunk('configuration/scripts', async (params) => {
  return await apiInstance.get('/scripts', { params });
});

export const scriptsOptionsGet = createAsyncThunk('configuration/scripts-options', async () => {
  return await apiInstance.get('/scripts');
});

export const scriptsGetById = createAsyncThunk('configuration/scripts-id', async (id) => {
  return await apiInstance.get(`/scripts/${id}`);
});

export const scriptsDelete = createAsyncThunk('configuration/scripts-delete', async (id) => {
  return await apiInstance.delete(`/scripts/${id}`);
});

export const scriptsAdd = createAsyncThunk('configuration/scripts-add', async (payload) => {
  return await apiInstance.post('/scripts', payload);
});

export const scriptsUpdate = createAsyncThunk('configuration/scripts-update', async (payload) => {
  const { id, formData } = payload;
  return await apiInstance.put(`/scripts/${id}`, formData);
});

export const whatsappListGet = createAsyncThunk(
  'configuration/whatsapp/whatsapp-list',
  async (params) => {
    return await apiInstance.get('/whatsapp/whatsapp-list', { params });
  }
);

export const whatsappQrGet = createAsyncThunk('configuration/whatsapp/get-qr', async (params) => {
  return await apiInstance.get('/whatsapp/get-qr');
});

export const whatsappDelete = createAsyncThunk(
  'configuration/whatsapp/delete-whatsapp',
  async (id) => {
    return await apiInstance.delete(`/whatsapp/delete-whatsapp/${id}`);
  }
);

export const whatsappQrUpdate = createAsyncThunk(
  'configuration/whatsapp/edit-whatsapp',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/whatsapp/edit-whatsapp/${id}`, rest);
  }
);

export const whatsappTemplatesGet = createAsyncThunk(
  'configuration/get-template',
  async (params) => {
    return await apiInstance.get('/whatsapp-template/', { params });
  }
);

export const whatsappTemplatesDelete = createAsyncThunk(
  'configuration/template-delete',
  async (id) => {
    return await apiInstance.delete(`/whatsapp-template/${id}`);
  }
);

export const whatsappTemplatesAdd = createAsyncThunk(
  'configuration/add-template',
  async (payload) => {
    return await apiInstance.post('/whatsapp-template/', payload);
  }
);

export const whatsappTemplatesUpdate = createAsyncThunk(
  'configuration/template-update',
  async (payload) => {
    const { id, formData } = payload;
    return await apiInstance.put(`/whatsapp-template/${id}`, formData);
  }
);

export const activeBroadcastGet = createAsyncThunk(
  'configuration/whatsapp-broadcast-active',
  async (params) => {
    return await apiInstance.get('/whatsapp_broadcast/', { params });
  }
);

export const inActiveBroadcastGet = createAsyncThunk(
  'configuration/lists/whatsapp-broadcast-inactive',
  async (params) => {
    return await apiInstance.get('/whatsapp_broadcast/inactive', { params });
  }
);

export const broadcastStatusUpdate = createAsyncThunk(
  'configuration/broadcast-status-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/whatsapp_broadcast/status_update/${id}`, rest);
  }
);

export const broadcastAdd = createAsyncThunk('configuration/broadcast-add', async (payload) => {
  return await apiInstance.post('/whatsapp_broadcast/', payload);
});

export const broadcastUpdate = createAsyncThunk(
  'configuration/broadcast-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/whatsapp_broadcast/${id}`, rest);
  }
);

export const broadcastDelete = createAsyncThunk('configuration/broadcast-delete', async (id) => {
  return await apiInstance.delete(`/whatsapp_broadcast/${id}`);
});

export const broadcastDetailsGetById = createAsyncThunk(
  'configuration/broadcast-by-id',
  async (id) => {
    return await apiInstance.get(`/whatsapp_broadcast/${id}`);
  }
);

export const broadcastMappingList = createAsyncThunk(
  'configuration/whatsapp_mapping_list',
  async (id) => {
    return await apiInstance.get(`/whatsapp_broadcast/mapping_list/${id}`);
  }
);

export const broadcastUploadCsv = createAsyncThunk(
  'configuration/whatsapp_upload_csv',
  async (payload) => {
    return await apiInstance.post('/whatsapp_broadcast/upload_csv', payload);
  }
);

export const broadcastDownload = createAsyncThunk(
  'configuration/whatsapp_broadcast_download',
  async (params) => {
    return await apiInstance.get('/whatsapp_broadcast/download', { params, responseType: 'blob' });
  }
);

export const whatsappBotTemplatesGet = createAsyncThunk(
  'configuration/whatsapp-bot-template',
  async (params) => {
    return await apiInstance.get('/whatsapp-bot-template/', { params });
  }
);

export const whatsappBotTemplatesOptionsGet = createAsyncThunk(
  'configuration/whatsapp-bot-template-options',
  async (params) => {
    return await apiInstance.get('/whatsapp-bot-template/', { params });
  }
);

export const whatsappBotTemplatesDelete = createAsyncThunk(
  'configuration/whatsapp-bot-delete',
  async (id) => {
    return await apiInstance.delete(`/whatsapp-bot-template/${id}`);
  }
);

export const whatsappBotTemplatesAdd = createAsyncThunk(
  'configuration/whatsapp-bot-add',
  async (payload) => {
    return await apiInstance.post('/whatsapp-bot-template/', payload);
  }
);

export const whatsappBotTemplatesUpdate = createAsyncThunk(
  'configuration/whatsapp-bot-update',
  async (payload) => {
    const { id, formData } = payload;
    return await apiInstance.put(`/whatsapp-bot-template/${id}`, formData);
  }
);

export const whatsappBotFlowGet = createAsyncThunk(
  'configuration/whatsapp-bot-flow-template',
  async (params) => {
    return await apiInstance.get('/whatsapp-bot-flow/', { params });
  }
);

export const whatsappBotFlowDelete = createAsyncThunk(
  'configuration/whatsapp-bot-flow-delete',
  async (id) => {
    return await apiInstance.delete(`/whatsapp-bot-flow/${id}`);
  }
);

export const whatsappBotFlowAdd = createAsyncThunk(
  'configuration/whatsapp-bot-flow-add',
  async (payload) => {
    return await apiInstance.post('/whatsapp-bot-flow/', payload);
  }
);

export const whatsappBotFlowUpdate = createAsyncThunk(
  'configuration/whatsapp-bot-flow-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/whatsapp-bot-flow/${id}`, rest);
  }
);

export const activeBotBroadcastGet = createAsyncThunk(
  'configuration/whatsapp-bot-broadcast-active',
  async (params) => {
    return await apiInstance.get('/whatsapp-bot-broadcast/', { params });
  }
);

export const inActiveBotBroadcastGet = createAsyncThunk(
  'configuration/lists/whatsapp-bot-broadcast-inactive',
  async (params) => {
    return await apiInstance.get('/whatsapp-bot-broadcast/inactive', { params });
  }
);

export const botBroadcastStatusUpdate = createAsyncThunk(
  'configuration/whatsapp-bot-broadcast-status-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/whatsapp-bot-broadcast/status_update/${id}`, rest);
  }
);

export const botBroadcastAdd = createAsyncThunk(
  'configuration/whatsapp-bot-broadcast-add',
  async (payload) => {
    return await apiInstance.post('/whatsapp-bot-broadcast/', payload);
  }
);

export const botBroadcastUpdate = createAsyncThunk(
  'configuration/whatsapp-bot-broadcast-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/whatsapp-bot-broadcast/${id}`, rest);
  }
);

export const botBroadcastDelete = createAsyncThunk(
  'configuration/whatsapp-bot-broadcast-delete',
  async (id) => {
    return await apiInstance.delete(`/whatsapp-bot-broadcast/${id}`);
  }
);

export const botBroadcastDetailsGetById = createAsyncThunk(
  'configuration/whatsapp-bot-broadcast-by-id',
  async (id) => {
    return await apiInstance.get(`/whatsapp-bot-broadcast/${id}`);
  }
);

export const botBroadcastMappingList = createAsyncThunk(
  'configuration/whatsapp-bot-broadcast_mapping_list',
  async (id) => {
    return await apiInstance.get(`/whatsapp-bot-broadcast/mapping_list/${id}`);
  }
);

export const botBroadcastUploadCsv = createAsyncThunk(
  'configuration/whatsapp-bot-broadcast_upload_csv',
  async (payload) => {
    return await apiInstance.post('/whatsapp-bot-broadcast/upload_csv', payload);
  }
);

export const botBroadcastDownload = createAsyncThunk(
  'configuration/whatsapp-bot-broadcast_download',
  async (params) => {
    return await apiInstance.get('/whatsapp-bot-broadcast/download', {
      params,
      responseType: 'blob'
    });
  }
);

export const whatsappMetaTemplatesGet = createAsyncThunk(
  'configuration/whatsapp-meta-template',
  async (params) => {
    return await apiInstance.get('/whatsapp-meta-template/', { params });
  }
);

export const whatsappMetaTemplatesOptionsGet = createAsyncThunk(
  'configuration/whatsapp-meta-template-options',
  async (params) => {
    return await apiInstance.get('/whatsapp-meta-template/', { params });
  }
);

export const whatsappMetaNumberOptionsGet = createAsyncThunk(
  'configuration/whatsapp-meta/whatsapp-meta-number',
  async (params) => {
    return await apiInstance.get('/whatsapp-meta/whatsapp-meta-number/', { params });
  }
);

export const metaTemplatesOptionsGet = createAsyncThunk(
  'configuration/meta-template-options',
  async (params) => {
    return await apiInstance.get('/whatsapp-meta/template', { params });
  }
);

export const whatsappMetaTemplatesDelete = createAsyncThunk(
  'configuration/whatsapp-bot-delete',
  async (id) => {
    return await apiInstance.delete(`/whatsapp-meta-template/${id}`);
  }
);

export const whatsappMetaTemplatesAdd = createAsyncThunk(
  'configuration/whatsapp-bot-add',
  async (payload) => {
    return await apiInstance.post('/whatsapp-meta-template/', payload);
  }
);

export const whatsappMetaTemplatesUpdate = createAsyncThunk(
  'configuration/whatsapp-bot-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/whatsapp-meta-template/${id}`, rest);
  }
);

export const whatsappMetaFlowGet = createAsyncThunk(
  'configuration/whatsapp-meta-flow-template',
  async (params) => {
    return await apiInstance.get('/whatsapp-meta-flow/', { params });
  }
);

export const whatsappMetaFlowDelete = createAsyncThunk(
  'configuration/whatsapp-meta-flow-delete',
  async (id) => {
    return await apiInstance.delete(`/whatsapp-meta-flow/${id}`);
  }
);

export const whatsappMetaFlowAdd = createAsyncThunk(
  'configuration/whatsapp-meta-flow-add',
  async (payload) => {
    return await apiInstance.post('/whatsapp-meta-flow/', payload);
  }
);

export const whatsappMetaFlowUpdate = createAsyncThunk(
  'configuration/whatsapp-meta-flow-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/whatsapp-meta-flow/${id}`, rest);
  }
);

export const activeMetaBroadcastGet = createAsyncThunk(
  'configuration/whatsapp-meta-broadcast-active',
  async (params) => {
    return await apiInstance.get('/whatsapp-meta-broadcast/', { params });
  }
);

export const inActiveMetaBroadcastGet = createAsyncThunk(
  'configuration/lists/whatsapp-meta-broadcast-inactive',
  async (params) => {
    return await apiInstance.get('/whatsapp-meta-broadcast/inactive', { params });
  }
);

export const metaBroadcastStatusUpdate = createAsyncThunk(
  'configuration/whatsapp-meta-broadcast-status-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/whatsapp-meta-broadcast/status_update/${id}`, rest);
  }
);

export const metaBroadcastAdd = createAsyncThunk(
  'configuration/whatsapp-meta-broadcast-add',
  async (payload) => {
    return await apiInstance.post('/whatsapp-meta-broadcast/', payload);
  }
);

export const metaBroadcastUpdate = createAsyncThunk(
  'configuration/whatsapp-meta-broadcast-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/whatsapp-meta-broadcast/${id}`, rest);
  }
);

export const metaBroadcastDelete = createAsyncThunk(
  'configuration/whatsapp-meta-broadcast-delete',
  async (id) => {
    return await apiInstance.delete(`/whatsapp-meta-broadcast/${id}`);
  }
);

export const metaBroadcastDetailsGetById = createAsyncThunk(
  'configuration/whatsapp-meta-broadcast-by-id',
  async (id) => {
    return await apiInstance.get(`/whatsapp-meta-broadcast/${id}`);
  }
);

export const metaBroadcastMappingList = createAsyncThunk(
  'configuration/whatsapp-meta-broadcast_mapping_list',
  async (id) => {
    return await apiInstance.get(`/whatsapp-meta-broadcast/mapping_list/${id}`);
  }
);

export const metaBroadcastUploadCsv = createAsyncThunk(
  'configuration/whatsapp-meta-broadcast_upload_csv',
  async (payload) => {
    return await apiInstance.post('/whatsapp-meta-broadcast/upload_csv', payload);
  }
);

export const metaBroadcastDownload = createAsyncThunk(
  'configuration/whatsapp-meta-broadcast_download',
  async (params) => {
    return await apiInstance.get('/whatsapp-meta-broadcast/download', {
      params,
      responseType: 'blob'
    });
  }
);

export const whatsappMetaListGet = createAsyncThunk(
  'configuration/whatsapp-meta-configuration',
  async (params) => {
    return await apiInstance.get('/whatsapp-meta-configuration', { params });
  }
);

export const whatsappMetaDetailsGetById = createAsyncThunk(
  'configuration/whatsapp-meta-configuration-by-id',
  async (id) => {
    return await apiInstance.get(`/whatsapp-meta-configuration/${id}`);
  }
);

export const whatsappMetaAdd = createAsyncThunk(
  'configuration/whatsapp-meta-configuration-add',
  async (payload) => {
    return await apiInstance.post('/whatsapp-meta-configuration', payload);
  }
);

export const whatsappMetaUpdate = createAsyncThunk(
  'configuration/whatsapp-meta-configuration-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/whatsapp-meta-configuration/${id}`, rest);
  }
);

export const whatsappMetaDelete = createAsyncThunk(
  'configuration/whatsapp-meta-configuration-delete',
  async (id) => {
    return await apiInstance.delete(`/whatsapp-meta-configuration/${id}`);
  }
);

export const smtpListGet = createAsyncThunk('configuration/smtp-config', async (params) => {
  return await apiInstance.get('/smtp-config', { params });
});

export const smtpDetailsGetById = createAsyncThunk(
  'configuration/smtp-config-by-id',
  async (id) => {
    return await apiInstance.get(`/smtp-config/${id}`);
  }
);

export const smtpAdd = createAsyncThunk('configuration/smtp-config-add', async (payload) => {
  return await apiInstance.post('/smtp-config', payload);
});

export const smtpUpdate = createAsyncThunk('configuration/smtp-config-update', async (payload) => {
  const { id, ...rest } = payload;
  return await apiInstance.put(`/smtp-config/${id}`, rest);
});

export const smtpDelete = createAsyncThunk('configuration/smtp-config-delete', async (id) => {
  return await apiInstance.delete(`/smtp-config/${id}`);
});

export const hotkeyListGet = createAsyncThunk('configuration/hot-key', async (params) => {
  return await apiInstance.get('/hot-key', { params });
});

export const hotkeyDetailsGetById = createAsyncThunk('configuration/hot-key-by-id', async (id) => {
  return await apiInstance.get(`/hot-key/${id}`);
});

export const hotkeyAdd = createAsyncThunk('configuration/hot-key-add', async (payload) => {
  return await apiInstance.post('/hot-key', payload);
});

export const hotkeyUpdate = createAsyncThunk('configuration/hot-key-update', async (payload) => {
  const { id, ...rest } = payload;
  return await apiInstance.put(`/hot-key/${id}`, rest);
});

export const hotkeyDelete = createAsyncThunk('configuration/hot-key-delete', async (id) => {
  return await apiInstance.delete(`/hot-key/${id}`);
});

export const aiAgentListGet = createAsyncThunk('configuration/ai-agent', async (params) => {
  return await apiInstance.get('/ai-agent', { params });
});

export const aiAgentDetailsGetById = createAsyncThunk(
  'configuration/ai-agent-by-id',
  async (id) => {
    return await apiInstance.get(`/ai-agent/${id}`);
  }
);

export const aiAgentAdd = createAsyncThunk('configuration/ai-agent-add', async (payload) => {
  return await apiInstance.post('/ai-agent', payload);
});

export const aiAgentUpdate = createAsyncThunk('configuration/ai-agent-update', async (payload) => {
  const { id, ...rest } = payload;
  return await apiInstance.put(`/ai-agent/${id}`, rest);
});

export const aiAgentDelete = createAsyncThunk('configuration/ai-agent-delete', async (id) => {
  return await apiInstance.delete(`/ai-agent/${id}`);
});

export const callexAiAgentOptionsGet = createAsyncThunk(
  'configuration/callex-ai-agent-options',
  async () => {
    return await apiInstance.get('/callex-ai-agent');
  }
);

export const callexAiAgentListGet = createAsyncThunk(
  'configuration/callex-ai-agent',
  async (params) => {
    return await apiInstance.get('/callex-ai-agent', { params });
  }
);

export const callexAiAgentDetailsGetById = createAsyncThunk(
  'configuration/callex-ai-agent-by-id',
  async (id) => {
    return await apiInstance.get(`/callex-ai-agent/${id}`);
  }
);

export const callexAiAgentAdd = createAsyncThunk(
  'configuration/callex-ai-agent-add',
  async (payload) => {
    return await apiInstance.post('/callex-ai-agent/', payload);
  }
);

export const callexAiAgentUpdate = createAsyncThunk(
  'configuration/callex-ai-agent-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/callex-ai-agent/${id}`, rest);
  }
);

export const callexAiAgentDelete = createAsyncThunk(
  'configuration/callex-ai-agent-delete',
  async (id) => {
    return await apiInstance.delete(`/callex-ai-agent/${id}`);
  }
);

export const callexVoiceListGet = createAsyncThunk('configuration/callex-voice-list', async () => {
  return await apiInstance.get('/callex-voice/voices/list');
});

export const voiceProvidersOptionsGet = createAsyncThunk('configuration/provider/tts', async () => {
  return await apiInstance.get('/provider/tts');
});

export const modalProviderOptionsGet = createAsyncThunk('configuration/provider/llms', async () => {
  return await apiInstance.get('provider/llms');
});

export const sttProviderOptionsGet = createAsyncThunk('configuration/provider/stt', async () => {
  return await apiInstance.get('provider/stt');
});

export const voiceListGet = createAsyncThunk('configuration/voice-list', async (params) => {
  return await apiInstance.get('provider/voices', { params });
});

export const aiAgentOptionsGet = createAsyncThunk('configuration/ai-agent-options', async () => {
  return await apiInstance.get('/ai-agent');
});

export const aiPhoneNumberOptionsGet = createAsyncThunk(
  'configuration/ai-phone-number-options',
  async () => {
    return await apiInstance.get('/phone-number');
  }
);

export const aiPhoneNumberListGet = createAsyncThunk(
  'configuration/ai-phone-number',
  async (params) => {
    return await apiInstance.get('/phone-number', { params });
  }
);

export const aiPhoneNumberDetailsGetById = createAsyncThunk(
  'configuration/ai-phone-number-by-id',
  async (id) => {
    return await apiInstance.get(`/phone-number/${id}`);
  }
);

export const aiPhoneNumberAdd = createAsyncThunk(
  'configuration/ai-phone-number-add',
  async (payload) => {
    const { formType, ...rest } = payload;
    return await apiInstance.post(`/phone-number/import/${formType}`, rest);
  }
);

export const aiPhoneNumberDetach = createAsyncThunk(
  'configuration/ai-phone-number-detach',
  async (payload) => {
    return await apiInstance.post('/phone-number/detach', payload);
  }
);

export const aiPhoneNumberAttach = createAsyncThunk(
  'configuration/ai-phone-number-attach',
  async (payload) => {
    return await apiInstance.post('/phone-number/attach', payload);
  }
);

export const aiPhoneNumberUpdate = createAsyncThunk(
  'configuration/ai-phone-number-update',
  async (payload) => {
    const { phoneNumberId } = payload;
    return await apiInstance.post(`/phone-number/${phoneNumberId}`, payload);
  }
);

export const aiPhoneNumberDelete = createAsyncThunk(
  'configuration/ai-phone-number-delete',
  async (id) => {
    return await apiInstance.delete(`/phone-number/${id}`);
  }
);

export const aiKeyListGet = createAsyncThunk('configuration/ai-key-list', async (params) => {
  return await apiInstance.get('/callex-apikey/', { params });
});

export const aiKeyAdd = createAsyncThunk('configuration/ai-key-add', async (payload) => {
  return await apiInstance.post('/callex-apikey/', payload);
});

export const aiKeyDelete = createAsyncThunk('configuration/ai-key-delete', async (id) => {
  return await apiInstance.delete(`/callex-apikey/${id}`);
});


const initialState = {
  crmsList: null,
  smtpList: null,
  hotkeyList: null,
  aiAgentList: null,
  aiPhoneNumberList: null,
  callexAiAgentList: null,
  callexVoiceList: [],
  whatsappMetaList: null,
  userList: null,
  processList: null,
  activeList: null,
  inActiveList: null,
  breakList: null,
  trunkList: null,
  outboundList: null,
  scriptsList: null,
  leadRetriesList: null,
  whatsappList: null,
  whatsappTemplatesList: null,
  whatsappBotTemplatesList: null,
  whatsappMetaTemplatesList: null,
  whatsappBotFlowList: null,
  whatsappMetaFlowList: null,
  broadcastActiveList: null,
  broadcastInActiveList: null,
  botBroadcastActiveList: null,
  botBroadcastInActiveList: null,
  metaBroadcastActiveList: null,
  metaBroadcastInActiveList: null,
  voiceProvidersOptions: [],
  modalProviderOptions: [],
  sttProviderOptions: [],
  aiAgentOptions: [],
  aiKeyList: null
};

const configurationSlice = createSlice({
  name: 'configuration',
  initialState,
  reducers: {
    clearConfigurationSlice: () => initialState,
    updateProcessList: (state, action) => {
      state.processList = action.payload;
    },
    updateActiveList: (state, action) => {
      state.activeList = action.payload;
    },
    updateInActiveList: (state, action) => {
      state.inActiveList = action.payload;
    },
    updateBroadcastActiveList: (state, action) => {
      state.broadcastActiveList = action.payload;
    },
    updateBroadcastInActiveList: (state, action) => {
      state.broadcastInActiveList = action.payload;
    },
    updateBotBroadcastActiveList: (state, action) => {
      state.botBroadcastActiveList = action.payload;
    },
    updateBotBroadcastInActiveList: (state, action) => {
      state.botBroadcastInActiveList = action.payload;
    },
    updateMetaBroadcastActiveList: (state, action) => {
      state.metaBroadcastActiveList = action.payload;
    },
    updateMetaBroadcastInActiveList: (state, action) => {
      state.metaBroadcastInActiveList = action.payload;
    },
    updateAiPhoneNumberList: (state, action) => {
      state.aiPhoneNumberList = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(crmsListGet.fulfilled, (state, action) => {
      state.crmsList = action?.payload;
    });
    builder.addCase(whatsappMetaListGet.fulfilled, (state, action) => {
      state.whatsappMetaList = action?.payload;
    });
    builder.addCase(userListGet.fulfilled, (state, action) => {
      state.userList = action?.payload;
    });
    builder.addCase(processListGet.fulfilled, (state, action) => {
      state.processList = action?.payload;
    });
    builder.addCase(activeListGet.fulfilled, (state, action) => {
      state.activeList = action?.payload;
    });
    builder.addCase(inActiveListGet.fulfilled, (state, action) => {
      state.inActiveList = action?.payload;
    });
    builder.addCase(breakListGet.fulfilled, (state, action) => {
      state.breakList = action?.payload;
    });
    builder.addCase(trunkListGet.fulfilled, (state, action) => {
      state.trunkList = action?.payload;
    });
    builder.addCase(outboundListGet.fulfilled, (state, action) => {
      state.outboundList = action?.payload;
    });
    builder.addCase(scriptsListGet.fulfilled, (state, action) => {
      state.scriptsList = action?.payload;
    });
    builder.addCase(leadRetriesListGet.fulfilled, (state, action) => {
      state.leadRetriesList = action?.payload;
    });
    builder.addCase(whatsappListGet.fulfilled, (state, action) => {
      state.whatsappList = action?.payload;
    });
    builder.addCase(whatsappTemplatesGet.fulfilled, (state, action) => {
      state.whatsappTemplatesList = action?.payload;
    });
    builder.addCase(activeBroadcastGet.fulfilled, (state, action) => {
      state.broadcastActiveList = action?.payload;
    });
    builder.addCase(inActiveBroadcastGet.fulfilled, (state, action) => {
      state.broadcastInActiveList = action?.payload;
    });
    builder.addCase(whatsappBotTemplatesGet.fulfilled, (state, action) => {
      state.whatsappBotTemplatesList = action?.payload;
    });
    builder.addCase(whatsappBotFlowGet.fulfilled, (state, action) => {
      state.whatsappBotFlowList = action?.payload;
    });
    builder.addCase(activeBotBroadcastGet.fulfilled, (state, action) => {
      state.botBroadcastActiveList = action?.payload;
    });
    builder.addCase(inActiveBotBroadcastGet.fulfilled, (state, action) => {
      state.botBroadcastInActiveList = action?.payload;
    });
    builder.addCase(whatsappMetaTemplatesGet.fulfilled, (state, action) => {
      state.whatsappMetaTemplatesList = action?.payload;
    });
    builder.addCase(whatsappMetaFlowGet.fulfilled, (state, action) => {
      state.whatsappMetaFlowList = action?.payload;
    });
    builder.addCase(activeMetaBroadcastGet.fulfilled, (state, action) => {
      state.metaBroadcastActiveList = action?.payload;
    });
    builder.addCase(inActiveMetaBroadcastGet.fulfilled, (state, action) => {
      state.metaBroadcastInActiveList = action?.payload;
    });
    builder.addCase(smtpListGet.fulfilled, (state, action) => {
      state.smtpList = action?.payload;
    });
    builder.addCase(hotkeyListGet.fulfilled, (state, action) => {
      state.hotkeyList = action?.payload;
    });
    builder.addCase(aiAgentListGet.fulfilled, (state, action) => {
      state.aiAgentList = action?.payload;
    });
    builder.addCase(aiPhoneNumberListGet.fulfilled, (state, action) => {
      state.aiPhoneNumberList = action?.payload;
    });
    builder.addCase(callexAiAgentListGet.fulfilled, (state, action) => {
      const { agents = [], pagination = {} } = action?.payload?.data || {};
      state.callexAiAgentList = {
        data: agents,
        ...pagination
      };
    });
    builder.addCase(callexVoiceListGet.fulfilled, (state, action) => {
      const data = action?.payload?.data?.voices || [];
      state.callexVoiceList = data.map((item) => ({
        label: item.name,
        value: item.id
      }));
    });
    builder.addCase(aiAgentOptionsGet.fulfilled, (state, action) => {
      const data = action?.payload?.data || [];
      state.aiAgentOptions = data.map((item) => ({
        label: item.name,
        value: item.id
      }));
    });
    builder.addCase(voiceProvidersOptionsGet.fulfilled, (state, action) => {
      const data = action?.payload?.data || [];
      state.voiceProvidersOptions = data.map((item) => ({
        label: item.name,
        value: item.name
      }));
    });
    builder.addCase(sttProviderOptionsGet.fulfilled, (state, action) => {
      const data = action?.payload?.data || [];
      state.sttProviderOptions = data.map((item) => ({
        label: item.name,
        value: item.name
      }));
    });
    builder.addCase(modalProviderOptionsGet.fulfilled, (state, action) => {
      const data = action?.payload?.data || [];

      const grouped = data.reduce((acc, item) => {
        const provider = item.provider_name;

        if (!acc[provider]) {
          acc[provider] = {
            label: provider.charAt(0).toUpperCase() + provider.slice(1),
            options: []
          };
        }

        acc[provider].options.push({
          label: item.name,
          value: item.name
        });

        return acc;
      }, {});

      state.modalProviderOptions = Object.values(grouped);
    });
    builder.addCase(aiKeyListGet.fulfilled, (state, action) => {
      state.aiKeyList = action?.payload;
    });
  }
});

export default configurationSlice.reducer;
export const {
  clearConfigurationSlice,
  updateProcessList,
  updateActiveList,
  updateInActiveList,
  updateBroadcastActiveList,
  updateBroadcastInActiveList,
  updateBotBroadcastActiveList,
  updateBotBroadcastInActiveList,
  updateMetaBroadcastActiveList,
  updateMetaBroadcastInActiveList,
  updateAiPhoneNumberList
} = configurationSlice.actions;

export const selectCrmsList = (state) => state.configuration.crmsList;
export const useCrmsList = () => {
  const crmsList = useSelector(selectCrmsList);
  return useMemo(() => crmsList, [crmsList]);
};

export const selectUserList = (state) => state.configuration.userList;
export const useUserList = () => {
  const userList = useSelector(selectUserList);
  return useMemo(() => userList, [userList]);
};

export const selectProcessList = (state) => state.configuration.processList;
export const useProcessList = () => {
  const processList = useSelector(selectProcessList);
  return useMemo(() => processList, [processList]);
};

export const selectActiveList = (state) => state.configuration.activeList;
export const useActiveList = () => {
  const activeList = useSelector(selectActiveList);
  return useMemo(() => activeList, [activeList]);
};

export const selectInActiveList = (state) => state.configuration.inActiveList;
export const useInActiveList = () => {
  const inActiveList = useSelector(selectInActiveList);
  return useMemo(() => inActiveList, [inActiveList]);
};

export const selectBreakList = (state) => state.configuration.breakList;
export const useBreakList = () => {
  const breakList = useSelector(selectBreakList);
  return useMemo(() => breakList, [breakList]);
};

export const selectTrunkList = (state) => state.configuration.trunkList;
export const useTrunkList = () => {
  const trunkList = useSelector(selectTrunkList);
  return useMemo(() => trunkList, [trunkList]);
};

export const selectOutboundList = (state) => state.configuration.outboundList;
export const useOutboundList = () => {
  const outboundList = useSelector(selectOutboundList);
  return useMemo(() => outboundList, [outboundList]);
};

export const selectScriptsList = (state) => state.configuration.scriptsList;
export const useScriptsList = () => {
  const scriptsList = useSelector(selectScriptsList);
  return useMemo(() => scriptsList, [scriptsList]);
};

export const selectLeadRetriesList = (state) => state.configuration.leadRetriesList;
export const useLeadRetriesList = () => {
  const leadRetriesList = useSelector(selectLeadRetriesList);
  return useMemo(() => leadRetriesList, [leadRetriesList]);
};

export const selectWhatsappList = (state) => state.configuration.whatsappList;
export const useWhatsappList = () => {
  const whatsappList = useSelector(selectWhatsappList);
  return useMemo(() => whatsappList, [whatsappList]);
};

export const selectWhatsappTemplates = (state) => state.configuration.whatsappTemplatesList;
export const useWhatsappTemplatesList = () => {
  const whatsappTemplatesList = useSelector(selectWhatsappTemplates);
  return useMemo(() => whatsappTemplatesList, [whatsappTemplatesList]);
};

export const selectBroadcastActiveList = (state) => state.configuration.broadcastActiveList;
export const useBroadcastActiveList = () => {
  const broadcastActiveList = useSelector(selectBroadcastActiveList);
  return useMemo(() => broadcastActiveList, [broadcastActiveList]);
};

export const selectBroadcastInActiveList = (state) => state.configuration.broadcastInActiveList;
export const useBroadcastInActiveList = () => {
  const broadcastInActiveList = useSelector(selectBroadcastInActiveList);
  return useMemo(() => broadcastInActiveList, [broadcastInActiveList]);
};

export const selectWhatsappBotTemplatesList = (state) =>
  state.configuration.whatsappBotTemplatesList;
export const useWhatsappBotTemplatesList = () => {
  const whatsappBotTemplatesList = useSelector(selectWhatsappBotTemplatesList);
  return useMemo(() => whatsappBotTemplatesList, [whatsappBotTemplatesList]);
};

export const selectWhatsappMetaTemplatesList = (state) =>
  state.configuration.whatsappMetaTemplatesList;
export const useWhatsappMetaTemplatesList = () => {
  const whatsappMetaTemplatesList = useSelector(selectWhatsappMetaTemplatesList);
  return useMemo(() => whatsappMetaTemplatesList, [whatsappMetaTemplatesList]);
};

export const selectWhatsappBotFlowList = (state) => state.configuration.whatsappBotFlowList;
export const useWhatsappBotFlowList = () => {
  const whatsappBotFlowList = useSelector(selectWhatsappBotFlowList);
  return useMemo(() => whatsappBotFlowList, [whatsappBotFlowList]);
};

export const selectWhatsappMetaFlowList = (state) => state.configuration.whatsappMetaFlowList;
export const useWhatsappMetaFlowList = () => {
  const whatsappMetaFlowList = useSelector(selectWhatsappMetaFlowList);
  return useMemo(() => whatsappMetaFlowList, [whatsappMetaFlowList]);
};

export const selectBotBroadcastActiveList = (state) => state.configuration.botBroadcastActiveList;
export const useBotBroadcastActiveList = () => {
  const botBroadcastActiveList = useSelector(selectBotBroadcastActiveList);
  return useMemo(() => botBroadcastActiveList, [botBroadcastActiveList]);
};

export const selectBotBroadcastInActiveList = (state) =>
  state.configuration.botBroadcastInActiveList;
export const useBotBroadcastInActiveList = () => {
  const botBroadcastInActiveList = useSelector(selectBotBroadcastInActiveList);
  return useMemo(() => botBroadcastInActiveList, [botBroadcastInActiveList]);
};

export const selectMetaBroadcastActiveList = (state) => state.configuration.metaBroadcastActiveList;
export const useMetaBroadcastActiveList = () => {
  const metaBroadcastActiveList = useSelector(selectMetaBroadcastActiveList);
  return useMemo(() => metaBroadcastActiveList, [metaBroadcastActiveList]);
};

export const selectMetaBroadcastInActiveList = (state) =>
  state.configuration.metaBroadcastInActiveList;
export const useMetaBroadcastInActiveList = () => {
  const metaBroadcastInActiveList = useSelector(selectMetaBroadcastInActiveList);
  return useMemo(() => metaBroadcastInActiveList, [metaBroadcastInActiveList]);
};

export const selectWhatsappMetaList = (state) => state.configuration.whatsappMetaList;
export const useWhatsappMetaList = () => {
  const whatsappMetaList = useSelector(selectWhatsappMetaList);
  return useMemo(() => whatsappMetaList, [whatsappMetaList]);
};

export const selectSmtpList = (state) => state.configuration.smtpList;
export const useSmtpList = () => {
  const smtpList = useSelector(selectSmtpList);
  return useMemo(() => smtpList, [smtpList]);
};

export const selectHotkeyList = (state) => state.configuration.hotkeyList;
export const useHotkeyList = () => {
  const hotkeyList = useSelector(selectHotkeyList);
  return useMemo(() => hotkeyList, [hotkeyList]);
};

export const selectAiAgentList = (state) => state.configuration.aiAgentList;
export const useAiAgentList = () => {
  const aiAgentList = useSelector(selectAiAgentList);
  return useMemo(() => aiAgentList, [aiAgentList]);
};

export const selectVoiceProvidersOptions = (state) => state.configuration.voiceProvidersOptions;
export const useVoiceProvidersOptions = () => {
  const voiceProvidersOptions = useSelector(selectVoiceProvidersOptions);
  return useMemo(() => voiceProvidersOptions, [voiceProvidersOptions]);
};

export const selectModalProviderOptions = (state) => state.configuration.modalProviderOptions;
export const useModalProviderOptions = () => {
  const modalProviderOptions = useSelector(selectModalProviderOptions);
  return useMemo(() => modalProviderOptions, [modalProviderOptions]);
};

export const selectSttProviderOptions = (state) => state.configuration.sttProviderOptions;
export const useSttProviderOptions = () => {
  const sttProviderOptions = useSelector(selectSttProviderOptions);
  return useMemo(() => sttProviderOptions, [sttProviderOptions]);
};

export const selectAiPhoneNumberList = (state) => state.configuration.aiPhoneNumberList;
export const useAiPhoneNumberList = () => {
  const aiPhoneNumberList = useSelector(selectAiPhoneNumberList);
  return useMemo(() => aiPhoneNumberList, [aiPhoneNumberList]);
};

export const selectCallexAiAgentList = (state) => state.configuration.callexAiAgentList;
export const useCallexAiAgentList = () => {
  const callexAiAgentList = useSelector(selectCallexAiAgentList);
  return useMemo(() => callexAiAgentList, [callexAiAgentList]);
};

export const selectCallexVoiceList = (state) => state.configuration.callexVoiceList;
export const useCallexVoiceList = () => {
  const callexVoiceList = useSelector(selectCallexVoiceList);
  return useMemo(() => callexVoiceList, [callexVoiceList]);
};

export const selectAiAgentOptions = (state) => state.configuration.aiAgentOptions;
export const useAiAgentOptions = () => {
  const aiAgentOptions = useSelector(selectAiAgentOptions);
  return useMemo(() => aiAgentOptions, [aiAgentOptions]);
};

export const selectAiKeyList = (state) => state.configuration.aiKeyList;
export const useAiKeyList = () => {
  const aiKeyList = useSelector(selectAiKeyList);
  return useMemo(() => aiKeyList, [aiKeyList]);
};


